import React, { useState, useEffect } from 'react';
import { ArrowLeft, Map, ExternalLink } from 'lucide-react';
import { scrollToTop } from '../utils/scrollToTop';
import { getBlogPosts } from '../services/blog/blogService';
import { BlogPost } from '../../types/blog.types';

interface SitemapProps {
  onBack?: () => void;
}

export function Sitemap({ onBack }: SitemapProps) {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const posts = await getBlogPosts();
        setBlogPosts(posts);
      } catch (error) {
        console.error('Error loading blog posts:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadPosts();
  }, []);

  const handleBack = () => {
    scrollToTop();
    if (onBack) return onBack();
    window.location.href = '/';
  };

  const pages = [
    {
      title: 'Home',
      url: '/',
      description: 'Main landing page with all video editing tools',
      category: 'Main'
    },
    {
      title: 'About',
      url: '/about',
      description: 'Learn how Snip Rush works and our features',
      category: 'Information'
    },
    {
      title: 'Tutorials',
      url: '/tutorials',
      description: 'Video editing tutorials and guides',
      category: 'Learning'
    },
    {
      title: 'Contact Us',
      url: '/contact',
      description: 'Get help and support',
      category: 'Support'
    },
    {
      title: 'Privacy Policy',
      url: '/privacy',
      description: 'How we protect your privacy and data',
      category: 'Legal'
    },
    {
      title: 'Terms & Conditions',
      url: '/terms',
      description: 'Terms of service and usage guidelines',
      category: 'Legal'
    },
    {
      title: 'Blog',
      url: '/blog',
      description: 'Video editing tips, tutorials, and guides',
      category: 'Learning'
    },
    {
      title: "What's New",
      url: '/whats-new',
      description: 'Latest features, improvements, and updates',
      category: 'Information'
    }
  ];

  const tools = [
    {
      title: 'Caption Creator',
      url: '/auto-captions',
      description: 'Add professional captions with AI transcription',
      category: 'Video Tools'
    },
    {
      title: 'Video Editor',
      url: '/video-editor',
      description: 'Trim, cut, and edit videos with timeline editing',
      category: 'Video Tools'
    },
    {
      title: 'Advanced Composer',
      url: '/video-composer',
      description: 'Multi-track timeline editor with transitions',
      category: 'Video Tools'
    },
    {
      title: 'Shorts Clipper',
      url: '/shorts-clipper',
      description: 'Extract multiple clips from long videos',
      category: 'Video Tools'
    },
    {
      title: 'Thumbnail Creator',
      url: '/thumbnail',
      description: 'Design eye-catching thumbnails for YouTube',
      category: 'Design Tools'
    },
    {
      title: 'Banner Creator',
      url: '/banner',
      description: 'Create YouTube channel banners',
      category: 'Design Tools'
    },
    {
      title: 'Image to Video',
      url: '/image-to-video',
      description: 'Convert images to videos with transitions',
      category: 'Video Tools'
    },
    {
      title: 'GIF Creator',
      url: '/gif-creator',
      description: 'Convert videos to optimized GIFs',
      category: 'Video Tools'
    }
  ];

  const allPages = [...pages, ...tools];
  
  const categories = allPages.reduce((acc, page) => {
    if (!acc[page.category]) {
      acc[page.category] = [];
    }
    acc[page.category].push(page);
    return acc;
  }, {} as Record<string, typeof allPages>);

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="container mx-auto px-4 pt-8">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>
      </div>
      
      <div className="container mx-auto px-4 pb-16 max-w-6xl">
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <Map className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-5xl font-bold mb-6 text-gray-900">
            Sitemap
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Navigate through all pages and features of Snip Rush. Find everything you need to create amazing videos.
          </p>
        </div>

        {/* Pages by Category */}
        <div className="space-y-8 mb-16">
          {Object.entries(categories).map(([category, categoryPages]) => (
            <div key={category} className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
              <h2 className="text-2xl font-bold mb-6 text-blue-600">{category}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryPages.map((page) => (
                  <div key={page.url} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-gray-900">{page.title}</h3>
                      <ExternalLink className="w-4 h-4 text-gray-600" />
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{page.description}</p>
                    <a 
                      href={page.url}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      {page.url}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Video Editing Tools */}
        <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm mb-16">
          <h2 className="text-2xl font-bold mb-6 text-purple-600">Video Editing Tools</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map((tool, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors">
                <h3 className="text-lg font-semibold mb-2 text-gray-900">{tool.title}</h3>
                <p className="text-sm text-gray-600 mb-4">{tool.description}</p>
                <a 
                  href={tool.url}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  {tool.url}
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* Blog Posts */}
        <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm mb-16">
          <h2 className="text-2xl font-bold mb-6 text-green-600">Blog Posts</h2>
          {isLoading ? (
            <div className="text-center py-8 text-gray-600">Loading blog posts...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {blogPosts.map((post) => (
                <div key={post.id} className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors">
                  <h3 className="text-lg font-semibold mb-2 text-gray-900">{post.title}</h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">
                      {post.category.replace('-', ' ').toUpperCase()}
                    </span>
                    <span className="text-xs text-gray-500">
                      {post.readTime} min read
                    </span>
                  </div>
                  <a 
                    href={`/blog/${post.slug}`}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    /blog/{post.slug}
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Links */}
        <div className="bg-blue-600 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold mb-4 text-white">Quick Navigation</h2>
          <p className="text-lg text-white mb-6">
            Jump to the most popular sections of Snip Rush
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <a
              href="/"
              className="bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Start Creating
            </a>
            <a
              href="/tutorials"
              className="bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Learn Tutorials
            </a>
            <a
              href="/blog"
              className="bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Read Blog
            </a>
            <a
              href="/whats-new"
              className="bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              What's New
            </a>
            <a
              href="/contact"
              className="bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Get Support
            </a>
            <a
              href="/privacy"
              className="bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Privacy Info
            </a>
          </div>
        </div>

        <div className="text-center mt-12 text-sm text-gray-400">
          <p>Last updated: {new Date().toLocaleDateString()}</p>
          <p className="mt-2">
            This sitemap includes all public pages and features available on Snip Rush.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Sitemap;
